import React from "react";
import { motion } from "framer-motion";
import { BadgeCheck, Check, Loader2, Scale, Users } from "lucide-react";
import ModelIcon, { MODEL_BRAND } from "./dashboard/ModelIcon";

/**
 * DebateContextRail - who is arguing, and how far the debate has progressed.
 *
 * Every value is derived from props the page already holds (composition, round,
 * maxRounds, debateData). It fetches nothing and changes no state.
 */

const ROLE_LABEL = {
  advocate: "Advocate",
  critic: "Critic",
  strategist: "Strategist",
};

const ROLE_BLURB = {
  advocate: "Argues the position",
  critic: "Attacks the weaknesses",
  strategist: "Weighs the trade-offs",
};

export default function DebateContextRail({
  composition,
  round = 0,
  maxRounds = 0,
  hasResults = false,
  isRunning = false,
  sessionsCompleted = null,
  roundsLabel = "",
  method = "IRAC",
}) {
  const roles = composition?.roles || {};
  const entries = Object.entries(roles);
  const pct = maxRounds > 0 ? Math.min(100, Math.round((round / maxRounds) * 100)) : 0;

  return (
    <aside className="dw-ctx ds-scroll-slim" aria-label="Debate context">
      {/* ---- verdict (was a large card above the transcript) ---- */}
      {hasResults && !isRunning && (
        <section className="dw-ctx-sec dw-verdict">
          <span className="dw-verdict-badge">
            <BadgeCheck size={13} />
            Debate finished
          </span>
          <p className="dw-verdict-title">Successfully completed</p>
          <dl className="dw-verdict-list">
            {roundsLabel && (
              <div>
                <dt>Rounds</dt>
                <dd>{roundsLabel}</dd>
              </div>
            )}
            <div>
              <dt>Method</dt>
              <dd>{method}</dd>
            </div>
          </dl>
        </section>
      )}

      {/* ---- panel ---- */}
      <section className="dw-ctx-sec">
        <h3 className="dw-ctx-title">
          <Users size={13} />
          {composition?.label || "Debate panel"}
        </h3>

        {entries.length ? (
          <ul className="dw-panel-list">
            {entries.map(([role, model]) => (
              <li key={role} className="dw-panel-row">
                <span
                  className="dw-panel-badge"
                  style={{ background: MODEL_BRAND[model]?.color }}
                  aria-hidden="true"
                >
                  <ModelIcon model={model} size={13} />
                </span>
                <span className="dw-panel-meta">
                  <span className="dw-panel-role">{ROLE_LABEL[role] || role}</span>
                  <span className="dw-panel-blurb">{ROLE_BLURB[role] || ""}</span>
                </span>
                <span className="dw-panel-model">{model}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="dw-ctx-empty">Choose a panel to begin.</p>
        )}
      </section>

      {/* ---- progress ---- */}
      <section className="dw-ctx-sec">
        <h3 className="dw-ctx-title">
          <Scale size={13} />
          Progress
        </h3>

        <div className="dw-progress-head">
          <span className="dw-progress-value">
            {hasResults || isRunning ? round : 0}
            <span className="dw-progress-max"> / {maxRounds || "—"}</span>
          </span>
          <span className="dw-progress-label">rounds</span>
        </div>

        <div className="ds-progress dw-progress-rail">
          <motion.div
            className="ds-progress-bar"
            initial={false}
            animate={{ width: `${hasResults || isRunning ? pct : 0}%` }}
            transition={{ duration: 0.6, ease: [0.32, 0.72, 0, 1] }}
          />
        </div>

        <p className={`dw-status dw-status-${isRunning ? "run" : hasResults ? "done" : "idle"}`}>
          {isRunning ? (
            <>
              <Loader2 size={12} className="ds-spin" />
              Debate in progress
            </>
          ) : hasResults ? (
            <>
              <Check size={12} />
              Debate complete
            </>
          ) : (
            "Not started"
          )}
        </p>
      </section>

      {/* ---- platform ---- */}
      {sessionsCompleted !== null && sessionsCompleted >= 0 && (
        <section className="dw-ctx-sec">
          <h3 className="dw-ctx-title">Platform</h3>
          <p className="dw-platform-value">
            {sessionsCompleted.toLocaleString("en-US")}
          </p>
          <p className="dw-platform-label">debate sessions completed</p>
        </section>
      )}
    </aside>
  );
}
